import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  Skeleton,
  Grid,
  useMediaQuery,
  useTheme,
  Modal,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../services/redux/store";
import loanDocumentsServices from "../../services/api/service/loanDocumentsService";
import { useSelector as useReduxSelector } from "react-redux";
import { RootState as ReduxRootState } from "../../services/redux/store";
import { setStatusPopup } from "../../services/redux/slices/statusPopupSlice";
import documentIcon from "../../assets/svgIcons/document.svg";
import Eyecon from "../../assets/svgIcons/eye.svg";
import { formatDateToShortMonth } from "../../utils/dateFormatUtils";
import eligibilityCheckServices from "../../services/api/service/eligibilityCheckService";
import { decodeBase64FileToUrl } from "../../components/ui/StepperDyanmicForm/helper";
import { Close } from "@mui/icons-material";
import PDFViewer from "../../components/ui/PDFViewer";

// --- Interfaces ---
interface LoanDocument {
  id?: string;
  documentName?: string;
  uploadedDate?: string;
  docUrl?: string;
  uuid?: string;
  s3Location?: string;
}

interface LoanDocumentsProps {
  // Can be extended to accept a list of documents as props for reusability.
}

const LoanDocuments: React.FC<LoanDocumentsProps> = () => {
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch<AppDispatch>();
  const { fetchUserApplicationData, trackingId } = useReduxSelector(
    (state: ReduxRootState) => state.stepper,
  );
  const [loanDocuments, setLoanDocuments] = useState<LoanDocument[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewDocUrl, setViewDocUrl] = useState<string>("");
  const [viewDocMimeType, setViewDocMimeType] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);

  const handlePreviewDocument = async (docPath: string) => {
    const payload = {
      key: docPath,
    };
    try {
      const response = await eligibilityCheckServices.previewDocument(payload);
      if (response?.status == 200) {
        setViewDocUrl(response?.data?.data?.downloadUrl);
        setIsModalOpen(true);
      }
    } catch (error) {
      dispatch(
        setStatusPopup({
          status: "error",
          message: "This document is not available for view at the moment.",
        }),
      );
      console.error("Error previewing document:", error);
    }
  };

  const handlePreviewDownloadDocument = async (
    uuid: string,
    s3Location: string,
  ) => {
    let payload: any = {
      trackingId: trackingId,
    };
    if (uuid) {
      payload.uuid = uuid;
    }
    if (s3Location) {
      payload.s3location = s3Location;
    }
    if (uuid == null && s3Location == null) {
      setViewDocUrl("");
      setViewDocMimeType("pdf");
      setIsModalOpen(true);
      return;
    }
    try {
      const response = await eligibilityCheckServices.downloadDocument(payload);
      if (response?.status === 200) {
        const base64Data = response?.data?.data?.data ?? response?.data?.data;
        if (s3Location) {
          setViewDocUrl(base64Data);
        } else {
          const { url, mimeType } = decodeBase64FileToUrl(base64Data);
          setViewDocUrl(url);
          setViewDocMimeType(mimeType);
        }
        setIsModalOpen(true);
      }
    } catch (error) {
      dispatch(
        setStatusPopup({
          status: "error",
          message: "This document is not available for view at the moment.",
        }),
      );
      console.error("Error previewing document:", error);
    }
  };

  const handleShowImageMList = () => {
    return (
      <>
        <div>
          <Grid container spacing={2} sx={{ paddingBottom: "20px" }}>
            <Grid item xs={12}>
              <div
                className="image-container"
                style={{
                  border: "1px solid #ddd",
                  padding: "10px",
                  minWidth: isXsOrSm ? "300px" : "100%",
                  height: isXsOrSm ? "320px" : "80vh",
                  overflow: "auto",
                  marginTop: "39px",
                }}
              >
                {viewDocUrl ? (
                  viewDocMimeType?.includes("pdf") ||
                  viewDocUrl?.includes("pdf") ? (
                    <PDFViewer
                      pdfUrl={viewDocUrl}
                      partnerStartedJourney={
                        fetchUserApplicationData?.partnerStartedJourney
                      }
                    />
                  ) : (
                    <img src={viewDocUrl} alt="sample" />
                  )
                ) : (
                  <div>No preview available</div>
                )}
              </div>
            </Grid>
          </Grid>
        </div>
      </>
    );
  };

  useEffect(() => {
    const getLoanDocuments = async (applicationId: string) => {
      if (!applicationId) return;

      try {
        setLoading(true);
        const resp =
          await loanDocumentsServices.getLoanDocuments(applicationId);

        if (resp?.status === 200 && resp?.data?.data) {
          const apiDocs = resp?.data?.data as any[];
          const mappedDocs: LoanDocument[] = apiDocs?.map((item: any) => {
            const createdOnMs = item?.userDocs?.createdOn;
            const uploadedDate = createdOnMs
              ? formatDateToShortMonth(new Date(createdOnMs).toISOString())
              : "";

            return {
              id: item?.id,
              documentName: item?.documentName ?? "",
              uploadedDate,
              docUrl: item?.userDocs?.docUrl ?? "",
              uuid: item?.userDocs?.uuid ?? "",
              s3Location: item?.userDocs?.s3Location ?? "",
            };
          });

          setLoanDocuments(mappedDocs);
        }
      } catch (error: any) {
        console.log("error", error);
        setLoanDocuments([]); // Reset Loan Documents list on error
        dispatch(
          setStatusPopup({
            status: "error",
            message:
              error?.response?.data?.message || "Fetch Loan Documents failed",
          }),
        );
      } finally {
        setLoading(false);
      }
    };
    if (fetchUserApplicationData?.applicationId) {
      getLoanDocuments(fetchUserApplicationData?.applicationId);
    }
  }, [fetchUserApplicationData?.applicationId]);

  return (
    <>
      <div className="existing-application">
        <div className="card-contaner">
          <Typography
            variant="h6"
            fontWeight={600}
            mb={2}
            color="#333"
            fontSize="1.1rem"
          >
            Loan Documents
          </Typography>

          <List sx={{ p: 0 }}>
            {" "}
            {/* Remove default list padding */}
            {loading ? (
              Array.from({ length: 4 }).map((_, idx) => (
                <ListItem
                  key={`skeleton-${idx}`}
                  sx={{
                    mb: 1,
                    bgcolor: "#ffffff",
                    border: "1px solid #eee",
                    borderRadius: "8px",
                    py: 1.5,
                    px: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      flexGrow: 1,
                      minWidth: "200px",
                      width: "100%",
                    }}
                  >
                    <Skeleton variant="circular" width={24} height={24} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Skeleton variant="text" width="40%" height={20} />
                      <Skeleton variant="text" width="30%" height={16} />
                    </Box>
                    <ListItemSecondaryAction
                      sx={{
                        position: "static",
                        transform: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 1,
                        mt: { xs: 1, sm: 0 },
                        width: { xs: "100%", sm: "auto" },
                      }}
                    >
                      <Skeleton variant="rectangular" width={72} height={32} />
                    </ListItemSecondaryAction>
                  </Box>
                </ListItem>
              ))
            ) : loanDocuments.length === 0 ? (
              <ListItem
                sx={{
                  mb: 1,
                  bgcolor: "#ffffff",
                  border: "1px solid #eee",
                  borderRadius: "8px",
                  py: 2,
                  px: 2,
                }}
              >
                <Grid item xs={12}>
                  <Typography
                    sx={{ textAlign: "center" }}
                    variant="body2"
                    color="textSecondary"
                  >
                    No loan documents available.
                  </Typography>
                </Grid>
              </ListItem>
            ) : (
              loanDocuments?.map((doc) => (
                <ListItem
                  key={doc?.id}
                  sx={{
                    mb: 1, // Margin bottom between list items
                    bgcolor: "#ffffff", // White background for each item
                    border: "1px solid #eee", // Light grey border
                    borderRadius: "8px", // Rounded corners
                    py: 1.5, // Vertical padding
                    px: 2, // Horizontal padding
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between", // Distribute space
                    flexWrap: "wrap", // Allow wrapping on smaller screens
                  }}
                >
                  {/* Left Section: Icon and Text */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      flexGrow: 1,
                      minWidth: "200px",
                    }}
                  >
                    <img src={documentIcon} alt="Document Icon" />
                    <ListItemText
                      primary={doc?.documentName}
                      secondary={`Uploaded on ${doc?.uploadedDate}`}
                      primaryTypographyProps={{
                        fontWeight: 500,
                        fontSize: "0.95rem",
                        color: "#333",
                      }}
                      secondaryTypographyProps={{
                        fontSize: "0.8rem",
                        color: "#757575",
                      }}
                      sx={{ my: 0 }} // Remove default ListItemText vertical margin
                    />
                    {/* Right Section: Action Buttons */}
                    <ListItemSecondaryAction
                      sx={{
                        position: "static", // Remove absolute positioning
                        transform: "none", // Remove transform
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end", // Align buttons to the right
                        gap: 1, // Space between buttons
                        mt: { xs: 1, sm: 0 }, // Margin top on small screens if primary text wraps
                        width: { xs: "100%", sm: "auto" }, // Full width on small screens
                      }}
                    >
                      <Button
                        startIcon={
                          <img
                            src={Eyecon}
                            alt="View"
                            style={{
                              height: 16,
                              width: 16,
                            }}
                          />
                        }
                        onClick={() => {
                          const docUrl = doc?.docUrl ?? "";
                          const docUuid = doc?.uuid ?? "";
                          const docS3location = doc?.s3Location ?? "";
                          if (fetchUserApplicationData?.partnerStartedJourney) {
                            handlePreviewDownloadDocument(
                              docUuid,
                              docS3location,
                            );
                          } else {
                            handlePreviewDocument(docUrl);
                          }
                        }}
                        sx={{
                          textTransform: "none",
                          color: "#00b3b5",
                          "&:hover": {
                            borderColor: "#00999a",
                            bgcolor: "#e0f7fa",
                          },
                          px: 2,
                          py: 0.5,
                          mt: isXsOrSm ? 1 : 0,
                          fontSize: "14px",
                          fontWeight: 500,
                          minWidth: "auto",
                          whiteSpace: "nowrap",
                        }}
                      >
                        View
                      </Button>
                      {/*<Button
                      variant="outlined"
                      onClick={() => handleView(doc)}
                      sx={{
                        textTransform: "none",
                        borderColor: "#00b3b5",
                        color: "#00b3b5",
                        "&:hover": {
                          bgcolor: "#e0f7fa",
                          borderColor: "#00999a",
                        },
                        borderRadius: "8px",
                        px: 1.5,
                        py: 0.5,
                        fontSize: "0.8rem",
                        whiteSpace: "nowrap", // Prevent text wrap
                      }}
                    >
                      View
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => handleDownload(doc)}
                      sx={{
                        textTransform: "none",
                        borderColor: "#00b3b5",
                        color: "#00b3b5",
                        "&:hover": {
                          bgcolor: "#e0f7fa",
                          borderColor: "#00999a",
                        },
                        borderRadius: "8px",
                        px: 1.5,
                        py: 0.5,
                        fontSize: "0.8rem",
                        whiteSpace: "nowrap", // Prevent text wrap
                      }}
                    >
                      Download
                    </Button> */}
                    </ListItemSecondaryAction>
                  </Box>
                </ListItem>
              ))
            )}
          </List>
        </div>
      </div>

      <Modal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isXsOrSm ? "300px" : "80%",
            minHeight: isXsOrSm ? "380px" : "",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
          }}
        >
          <Close
            onClick={() => {
              setIsModalOpen(false);
            }}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
            }}
          />
          {handleShowImageMList()}
        </Box>
      </Modal>
    </>
  );
};

export default LoanDocuments;
